import React, { useState, useEffect } from 'react'
import { supabase } from './supabase'
import Login from './components/Login'
import Dashboard from './components/Dashboard'
import Clients from './components/Clients'
import Staff from './components/Staff'
import Schedule from './components/Schedule'
import Routes from './components/Routes'
import Financials from './components/Financials'
import Marketing from './components/Marketing'
import Stats from './components/Stats'
import Settings from './components/Settings'
import Payroll from './components/Payroll'
import StaffPortal from './components/StaffPortal'
import WorkOrderPublic from './components/WorkOrderPublic'
import Archive from './components/Archive'
import SMS from './components/SMS'
import Invoices from './components/Invoices'
import MapView from './components/MapView'
import Asistente from './components/Asistente'
import PortalManager from './components/PortalManager'
import Leads from './components/Leads'
import TeamActivity from './components/TeamActivity'
import Icon from './components/Icon'

const NAV = [
  { id:'dashboard',  icon:'home',       label:'Inicio' },
  { id:'schedule',   icon:'calendar',   label:'Horario' },
  { id:'routes',     icon:'route',      label:'Rutas' },
  { id:'map',        icon:'map',        label:'Mapa' },
  { id:'asistente',  icon:'bot',        label:'Asistente' },
  { id:'payroll',    icon:'payments',   label:'Nómina' },
  { id:'financials', icon:'bank',       label:'Finanzas' },
  { id:'clients',    icon:'group',      label:'Clientes' },
  { id:'leads',      icon:'person_add', label:'Leads' },
  { id:'staff',      icon:'badge',      label:'Personal' },
  { id:'team_activity', icon:'clock',   label:'Actividad' },
  { id:'marketing',  icon:'campaign',   label:'Marketing' },
  { id:'portal_mgr', icon:'phone',      label:'Portal' },
  { id:'stats',      icon:'chart',      label:'Estadísticas' },
  { id:'archive',    icon:'archive',    label:'Archivo' },
  { id:'sms',        icon:'sms',        label:'SMS' },
  { id:'invoices',   icon:'receipt',    label:'Facturas' },
  { id:'help',       icon:'help',       label:'Ayuda' },
]

const THEMES = {
  pink:   { primary:'#e8547a', light:'#fdeef3', bg:'#f8f9fa', border:'#e3e5e8', active:'#fdeef3', activeText:'#c03060' },
  purple: { primary:'#7F77DD', light:'#EEEDFE', bg:'#f8f7ff', border:'#e8e4f8', active:'#EEEDFE', activeText:'#3C3489' },
  teal:   { primary:'#1D9E75', light:'#E1F5EE', bg:'#f4fcf8', border:'#d4ede4', active:'#E1F5EE', activeText:'#085041' },
  dark:   { primary:'#e8547a', light:'#2d2d2d', bg:'#202124', border:'#333',    active:'#2d2d2d', activeText:'#e8547a' },
}

export default function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('dashboard')
  const [theme, setTheme] = useState(() => localStorage.getItem('crm_theme') || 'pink')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  useEffect(() => {
    const h = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])

  const T = THEMES[theme] || THEMES.pink

  // Check if this is a staff schedule link
  // Work order public signature page
  const workorderToken = new URLSearchParams(window.location.search).get('workorder')
  if (workorderToken) return <WorkOrderPublic token={workorderToken} />

  // Staff portal - accessible at /staff or ?portal=1
  const isPortal = window.location.pathname.includes('/staff') || window.location.search.includes('portal=1')
  if (isPortal) return <StaffPortal />

  const isStaffView = window.location.search.includes('route=')

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session); setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setSession(session))

    // Listen for tab navigation from child components
    function navHandler(e) { if (e.detail?.tab) setTab(e.detail.tab) }
    window.addEventListener('navigate-to', navHandler)
    return () => { subscription.unsubscribe(); window.removeEventListener('navigate-to', navHandler) }
  }, [])

  function changeTheme(t) { setTheme(t); localStorage.setItem('crm_theme', t) }
  async function signOut() { await supabase.auth.signOut() }

  // Staff read-only view — no auth needed
  // Old staff schedule links (?route=) now open the staff portal instead
  if (isStaffView) return <StaffPortal />

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#f8f9fa' }}>
      <div className="crm-logo" style={{ width:40, height:40, fontSize:18 }}>K</div>
    </div>
  )

  if (!session) return <Login />

  const isDark = theme === 'dark'
  const isMobile = windowWidth <= 768

  return (
    <div style={{ display:'flex', height:'100vh', overflow:'hidden', background: T.bg }}>

      {/* MOBILE HAMBURGER OVERLAY */}
      {isMobile && drawerOpen && (
        <div onClick={()=>setDrawerOpen(false)}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',zIndex:200}}/>
      )}

      {/* SIDEBAR */}
      <div className={isDark ? 'crm-side dark' : 'crm-side'} style={{
        width: 232,
        background: isDark ? '#111' : (T.bg),
        display:'flex', flexDirection:'column', flexShrink:0,
        ...(isMobile ? {
          position:'fixed', top:0, left:0, height:'100vh', zIndex:201, background: isDark ? '#111' : '#fff',
          transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition:'transform .25s cubic-bezier(.4,0,.2,1)',
          boxShadow: drawerOpen ? '4px 0 24px rgba(0,0,0,.15)' : 'none',
        } : {})
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'18px 20px 14px' }}>
          <div className="crm-logo">K</div>
          <div style={{ fontSize:16, fontWeight:500, color: isDark?'#fff':'#202124', letterSpacing:'.1px' }}>Kitty's Cleaning</div>
        </div>
        <nav style={{ flex:1, padding:'4px 12px 8px', display:'flex', flexDirection:'column', gap:1, overflowY:'auto' }}>
          {/* Always visible: Inicio, Horario, Clientes, Actividad */}
          {NAV.filter(n=>['dashboard','schedule','clients','team_activity'].includes(n.id)).map(n => (
            <NavItem key={n.id} n={n} active={tab===n.id} T={T} onClick={() => { setTab(n.id); setDrawerOpen(false) }} />
          ))}

          {/* Space break between the two sections */}
          <div style={{ height:1, background:'#e3e5e8', margin:'10px 14px' }}/>

          {/* Hidden from the menu, not deleted: Rutas, Nómina, Asistente, Portal, SMS, Ayuda */}
          {['map','financials','leads','staff','marketing','stats','archive','invoices'].map(id => {
            const n = NAV.find(x=>x.id===id); if(!n) return null
            return <NavItem key={n.id} n={n} active={tab===n.id} T={T} onClick={() => { setTab(n.id); setDrawerOpen(false) }} />
          })}

          <div style={{flex:1}}/>
        </nav>
        <div style={{ padding:'12px 16px 14px', display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:30, height:30, borderRadius:'50%', background:T.light, color:T.activeText, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:500, flexShrink:0 }}>K</div>
          <div style={{ fontSize:12, color:'#5f6368', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{session.user.email}</div>
          <button className="crm-icon-btn" onClick={() => { setTab('settings'); setDrawerOpen(false) }} title="Configuración"><Icon name="settings" size={20} /></button>
          <button className="crm-icon-btn" onClick={signOut} title="Salir"><Icon name="logout" size={20} /></button>
        </div>
      </div>

      {/* MAIN */}
      <div className={isMobile ? '' : 'crm-main'} style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:T.bg }}>
        {/* Mobile top bar */}
        {isMobile && (
          <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:'#fff',borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
            <button className="crm-icon-btn" onClick={()=>setDrawerOpen(true)} title="Menú">
              <Icon name="menu" size={24} />
            </button>
            <span style={{fontSize:17,fontWeight:500,color:'#202124'}}>
              {NAV.find(n=>n.id===tab)?.label || (tab==='settings' ? 'Configuración' : "Kitty's Cleaning")}
            </span>
          </div>
        )}
        {tab === 'dashboard'  && <Dashboard />}
        {tab === 'clients'    && <Clients />}
        {tab === 'leads'      && <Leads />}
        {tab === 'schedule'   && <Schedule onNavigate={setTab} />}
        {tab === 'routes'     && <Routes />}
        {tab === 'staff'      && <Staff />}
        {tab === 'team_activity' && <TeamActivity />}
        {tab === 'payroll'    && <Payroll />}
        {tab === 'map'        && <MapView />}
        {tab === 'financials' && <Financials />}
        {tab === 'marketing'  && <Marketing />}
        {tab === 'portal_mgr' && <PortalManager />}
        {tab === 'stats'      && <Stats />}
        {tab === 'archive'    && <Archive />}
        {tab === 'sms'        && <SMS />}
        {tab === 'invoices'    && <Invoices />}
        {tab === 'settings'   && <Settings theme={theme} themes={THEMES} onChange={changeTheme} />}
        {tab === 'asistente'  && <Asistente />}
        {tab === 'help'       && <Help />}
      </div>
    </div>
  )
}

function NavItem({ n, active, T, onClick }) {
  return (
    <button className={active ? 'crm-nav active' : 'crm-nav'} onClick={onClick}
      style={active ? { background: T.active, color: T.activeText } : undefined}>
      <Icon name={n.icon} size={20} />
      {n.label}
    </button>
  )
}

function Help() {
  const [open, setOpen] = useState(null)
  const [openCat, setOpenCat] = useState('Inicio')
  const faqs = [
    { cat:'Inicio', q:`¿Cómo funciona el sistema?`, a:`Este CRM maneja toda la operación de Kitty's Cleaning. Desde el menú lateral accedes a Horario para programar limpiezas, Rutas para asignar equipos, Nómina para pagos, Finanzas para ingresos y gastos, y más.` },
    { cat:'Inicio', q:`¿Cómo agrego una tarea?`, a:`En Inicio, escribe la tarea en el campo de texto, elige a quién asignarla (Todas, Yosi, o Mami), y presiona Agregar. Haz clic en cualquier tarea para ver detalles y cambiar urgencia o fecha límite.` },
    { cat:'Inicio', q:`¿Qué significan los colores en las tareas?`, a:`Rojo = urgencia alta, Amarillo = urgencia media, Gris = urgencia normal. Haz clic en la tarea para cambiar la urgencia.` },
    { cat:'Inicio', q:`¿Cómo uso el tablero de avisos?`, a:`Escribe el aviso y presiona Publicar. Usa 📍 para fijarlo arriba, o × para eliminarlo. Máximo 10 avisos por página.` },
    { cat:'Inicio', q:`¿Cómo veo solo mis tareas?`, a:`Usa los botones Yosi o Mami para filtrar. Esta semana ✓ muestra las completadas esta semana, Archivadas las de semanas anteriores.` },
    { cat:'Horario', q:`¿Cómo programo una limpieza?`, a:`Arrastra el nombre del cliente desde el panel izquierdo hacia el día y hora correcta. El cliente solo aparece si le toca limpieza esa semana según su frecuencia.` },
    { cat:'Horario', q:`¿Cómo cambio la hora de una limpieza?`, a:`Haz clic en el bloque en el calendario. Cambia hora, duración, ruta, o agrega una nota especial. Los cambios son solo para esa visita.` },
    { cat:'Horario', q:`¿Cómo agrego una estimación?`, a:`Usa el botón morado 📋 Estimación. Ingresa dirección, día y hora. Aparece como un bloque morado en el calendario asignado a Mami.` },
    { cat:'Horario', q:`¿Cómo programo un cliente masivamente?`, a:`Haz clic en 📅 Masivo. Selecciona el cliente, el mes, y toca todos los días que quieres. Presiona Crear X limpiezas y se crean todas de una vez.` },
    { cat:'Horario', q:`¿Por qué no aparece un cliente en el panel lateral?`, a:`El sistema calcula qué clientes les toca según su frecuencia y última limpieza. Si ya fue programado esa semana, desaparece del panel.` },
    { cat:'Rutas', q:`¿Cómo asigno trabajos a las rutas?`, a:`Arrastra los trabajos del panel Sin asignar hacia la ruta correcta. Luego toca los nombres del personal para asignarlos a esa ruta.` },
    { cat:'Rutas', q:`¿Cómo completo el día?`, a:`Presiona ✓ Completar día. Esto registra limpiezas en el historial, agrega ingresos a Finanzas, y actualiza la Nómina con horas planificadas automáticamente.` },
    { cat:'Rutas', q:`¿Cómo envío el horario al equipo?`, a:`En cada ruta hay un botón 🔗 Enlace. Cópialo y mándalo por WhatsApp. El equipo lo ve en su teléfono sin cuenta — muestra direcciones, códigos y notas.` },
    { cat:'Rutas', q:`¿Cómo marco la conductora?`, a:`Después de asignar personal aparece 🚗 Conductora. Toca el nombre de quien maneja. Sus millas se calculan y agregan a su nómina automáticamente.` },
    { cat:'Rutas', q:`¿Qué significa ⛔?`, a:`Conflicto de compatibilidad — la limpiadora asignada no es aceptada por ese cliente. Revisa Compatibilidad en el perfil del cliente.` },
    { cat:'Nómina', q:`¿Cómo funciona la nómina?`, a:`Cada empleada tiene campos por día: hrs (horas), flat (tarifa plana ese día), tip (propina), mi (millas). El total se calcula automáticamente.` },
    { cat:'Nómina', q:`¿Cómo registro una tarifa plana?`, a:`Escribe el monto en el campo morado del día. Reemplaza el cálculo por hora solo ese día. Los otros días siguen calculando normal.` },
    { cat:'Nómina', q:`¿Cómo genero el PDF de una empleada?`, a:`Presiona 📄 al final de su fila. Se abre la hoja de tiempo con horas, tarifas, propinas, millas y total. Usa Cmd+P para guardar como PDF.` },
    { cat:'Nómina', q:`¿Las horas se llenan automáticamente?`, a:`Sí. Al completar el día en Rutas, las horas se agregan automáticamente a la nómina de esa semana. Puedes editarlas si algo cambió.` },
    { cat:'Finanzas', q:`¿Cómo registro un ingreso o gasto?`, a:`En Finanzas, presiona + Agregar. Selecciona tipo, categoría, monto y fecha. Presiona Guardar.` },
    { cat:'Finanzas', q:`¿Cómo veo el resumen trimestral?`, a:`Usa los botones Q1, Q2, Q3, Q4. Haz clic en un mes para ver solo ese mes. PDF trimestral genera un reporte completo para tu contador.` },
    { cat:'Finanzas', q:`¿Cada cuánto hago el reporte trimestral?`, a:`Cada 3 meses: Ene-Mar, Abr-Jun, Jul-Sep, Oct-Dic. Genera el PDF al final de cada trimestre para tus impuestos estimados.` },
    { cat:'Clientes', q:`¿Cómo agrego un cliente?`, a:`Ve a Clientes y presiona + Nuevo cliente. Llena nombre, dirección, frecuencia, y demás datos. Presiona Guardar.` },
    { cat:'Clientes', q:`¿Cómo agrego el apodo?`, a:`En el perfil, campo Apodo en la sección Programación. Es lo que aparece en el calendario y las rutas.` },
    { cat:'Clientes', q:`¿Cómo corrijo la última fecha de limpieza?`, a:`En el perfil, sección Fechas de limpieza, campo Corregir última limpieza. Elige la fecha correcta y se agrega al historial.` },
    { cat:'Personal', q:`¿Cómo agrego una empleada?`, a:`Ve a Personal y presiona + Agregar personal. Llena nombre, rol, teléfono, tarifa y disponibilidad. Presiona Guardar.` },
    { cat:'Personal', q:`¿Qué es el aviso de domingo?`, a:`Cada domingo aparece un aviso con la disponibilidad de cada empleada. Puedes cambiar qué días trabajan y confirmar para empezar la semana.` },
    { cat:'Personal', q:`¿Cómo cambio la disponibilidad de una empleada?`, a:`Ve a Personal, selecciona la empleada, y en Disponibilidad toca cada día para activarlo o desactivarlo. Presiona Guardar.` },
    { cat:'General', q:`¿Puedo acceder desde mi teléfono?`, a:`Sí. Abre Chrome o Safari, ve a kittyscleaning.netlify.app e ingresa con tu correo y contraseña.` },
    { cat:'General', q:`¿Cómo cambio el color del sistema?`, a:`Haz clic en ⚙️ en la parte inferior del menú. Ahí cambias el tema de color y la fuente.` },
    { cat:'General', q:`¿Qué hago si algo no funciona?`, a:`Toma una foto de la pantalla y mándasela a tu hija por WhatsApp.` },
  ]


  const cats = [...new Set(faqs.map(f=>f.cat))]

  return (
    <div style={{ padding:'20px 24px', overflowY:'auto', height:'100%' }}>
      <div style={{ fontSize:20, fontWeight:600, marginBottom:4 }}>Centro de ayuda</div>
      <div style={{ fontSize:12, color:'#80868b', marginBottom:16 }}>{faqs.length} preguntas frecuentes</div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:20 }}>
        {cats.map(cat=>(
          <button key={cat} style={{ fontSize:11, padding:'5px 12px', borderRadius:20, border:'1px solid #e3e5e8', cursor:'pointer',
            background:openCat===cat?'#e8547a':'#fff', color:openCat===cat?'#fff':'#5f6368', fontWeight:openCat===cat?500:400 }}
            onClick={()=>{ setOpenCat(cat); setOpen(null) }}>
            {cat}
          </button>
        ))}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, alignItems:'start' }}>
        {faqs.filter(f=>f.cat===openCat).map((f,i)=>(
          <div key={i} style={{ border:'1px solid #e3e5e8', borderRadius:10, overflow:'hidden' }}>
            <div style={{ padding:'11px 14px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', background:open===i?'#fdeef3':'#fff' }}
              onClick={()=>setOpen(open===i?null:i)}>
              <span style={{ fontSize:12, fontWeight:500, color:open===i?'#c03060':'#202124', paddingRight:8 }}>{f.q}</span>
              <span style={{ color:'#bdc1c6', flexShrink:0, display:'inline-block', transform:open===i?'rotate(90deg)':'none', transition:'transform .2s' }}>›</span>
            </div>
            {open===i&&(
              <div style={{ padding:'10px 14px', fontSize:12, color:'#5f6368', lineHeight:1.7, background:'#fff', borderTop:'1px solid #e3e5e8' }}>{f.a}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

